import * as Yup from "yup";
import { validateCharacterLimitWithContext } from "../../../helpers/formBodyCharacterLimitValidation";

const today = new Date();


export const schema = Yup.object().shape({
  title: Yup.string().required("Debes indicar el nombre del encuentro."),
  description: Yup.string()
    .nullable()
    .required("Agrega una descripción a tu encuentro.")
    .test(
      'character-limit',
      'No puedes usar más de caracteres', // El mensaje se maneja dinámicamente en la función
      function (value) {
        return validateCharacterLimitWithContext.call(this, value, 2000); // 1000 caracteres máximo
      }
    ),
  join_url: Yup.string().matches(
    /^(http|https):\/\/[^\s/$.?#].[^\s]*$/,
    'Debe ser una URL válida con http:// o https:// por delante. Ejemplo https://zoom.us'
  ),
  speaker_name: Yup.array().of(Yup.string()).nullable(),
  speaker_profile_pic: Yup.array().nullable(),
  use_profile_info: Yup.array().of(Yup.string()).nullable(),
  cover_pic: Yup.array().nullable(),
  complementary_material_description: Yup.string()
    .nullable()
    .test(
      'character-limit',
      'No puedes usar más de caracteres', // El mensaje se maneja dinámicamente en la función
      function (value) {
        return validateCharacterLimitWithContext.call(this, value, 500); // 500 caracteres máximo
      }
    ),
  complementary_material_files: Yup.array().nullable(),
  complementary_material_downloadable: Yup.array().of(Yup.string()).nullable(),
  duration: Yup.number().min(0.1, "Debes indicar un numero mayor a 0").required("Debes indicar la duracion del Encuentro"),
  duration_type: Yup.string().required("Debes indicar la unidad de la duración del Encuentro"),
  session_datetime: Yup.date()
    // .min(today, "Debes indicar una fecha futura")
    .required("Indica la fecha del encuentro."),
  content_categories: Yup.array().of(Yup.number()).nullable(),
  recorded_session_url: Yup.string(),
  group: Yup.number().required(),
  session_reminders_settings: Yup.array().of(
    Yup.object().shape({
      exec_datetime: Yup.date()
        .nullable()
        .test(
          'is-before-session',
          'La fecha del recordatorio debe ser anterior a la fecha del encuentro.',
          function (value) {
            const { session_datetime } = this?.options?.context || {};
            return !value || !session_datetime || new Date(value) <= new Date(session_datetime);
          }
        )
        .when(['type', 'execution_type'], {
          is: (type: string, executionType: string) =>
            type === 'first_announcement' && executionType === 'in_future',
          then: (schema: any) =>
            schema.min(new Date(), 'Debes indicar una fecha futura').required("Indica una fecha"),
          otherwise: (schema: any) => schema
        })
    })
  ),
  recorded_session_notification: Yup.string()
    .nullable()
    .test(
      'character-limit',
      'El mensaje no puede superar los 500 caracteres', // El mensaje se maneja dinámicamente en la función
      function (value) {
        return validateCharacterLimitWithContext.call(this, value, 500); // 1000 caracteres máximo
      }
    )
});

interface FormNotificationRemindersValues {
  exec_time_before: number | null;
  exec_time_before_unit: string | null;
  enabled: string[];
  custom_message: string;
  execution_type: string;
  type: string;
  exec_datetime?: string;
  schedule_type?: string;
}

interface FormValues {
  title: string;
  description: string;
  join_url: string;
  speaker_name: string;
  speaker_profile_pic: object[];
  use_profile_info: string[];
  cover_pic: object[];
  complementary_material_description: string;
  complementary_material_files: object[];
  complementary_material_downloadable: string[];
  session_datetime: string;
  content_categories: number[];
  recorded_session_url: string;
  recorded_session_notification: string;
  group: number | null;
  will_be_recorded: string[];
  access_code: string;
  duration: number;
  duration_type: string;
  recorded_access_code: string;
  notificate_by_chat: string[];
  notificate_by_email: string[];
  session_reminders_settings: FormNotificationRemindersValues[];
}

export const initialValues: FormValues = {
  title: "",
  description: "",
  join_url: "",
  speaker_name: "",
  speaker_profile_pic: [],
  use_profile_info: ["true"],
  cover_pic: [],
  complementary_material_description: "",
  complementary_material_files: [],
  complementary_material_downloadable: [],
  session_datetime: "",
  content_categories: [],
  recorded_session_url: "",
  recorded_session_notification: "",
  group: null,
  will_be_recorded: ['true'],
  access_code: "",
  duration: 0,
  duration_type: "hour",
  recorded_access_code: "",
  notificate_by_chat: ["true"],
  notificate_by_email: ["true"],
  session_reminders_settings: [
    {
      exec_time_before: null,
      exec_time_before_unit: null,
      enabled: ['true'],
      custom_message: "",
      execution_type: "now",
      type: "first_announcement",
      schedule_type: "exact_datetime"
    },
    {
      exec_time_before: 24,
      exec_time_before_unit: "hour",
      enabled: ['false'],
      custom_message: "",
      execution_type: "in_future",
      type: "reminder",
      schedule_type: "time_before"
    },
    {
      exec_time_before: 1,
      exec_time_before_unit: "hour",
      enabled: ['false'],
      custom_message: "",
      execution_type: "in_future",
      type: "reminder",
      schedule_type: "time_before"
    },
    {
      exec_time_before: 30,
      exec_time_before_unit: "minute",
      enabled: ['false'],
      custom_message: "",
      execution_type: "in_future",
      type: "reminder",
      schedule_type: "time_before"
    },
    {
      exec_time_before: null,
      exec_time_before_unit: null,
      enabled: ['true'],
      custom_message: "",
      execution_type: "in_future",
      type: "open_room",
      exec_datetime: "",
      schedule_type: "exact_datetime"
    }
  ]
};